import React, { useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { UserProvider } from './context/userContext';
import { Outlet } from 'react-router-dom';
import { StaffProvider } from './context/StaffContext';
import ProtectedRoute from './components/protectedroute/ProtectedRoute';
import Attendance from './pages/Nurse/Attendance';
import './App.css';

// Lazy load components
const Login = lazy(() => import('./components/login/login'));
const AdminLogin = lazy(() => import('./components/admin/admin'));
const ForgotPassword = lazy(() => import('./components/admin/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/admin/ResetPassword'));
const StaffDashboard = lazy(() => import('./pages/staff/dashboard'));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const Dashboard = lazy(() => import('./pages/Admin/Dashboard'));
const LeadsManagement = lazy(() => import('./pages/Admin/LeadsManagement'));
const StaffManagement = lazy(() => import('./pages/Admin/StaffManagement'));
const ReportsAndAnalytics = lazy(() => import('./pages/Admin/ReportsAndAnalytics'));
const EventManagement = lazy(() => import('./pages/Admin/EventManagement'));
const Settings = lazy(() => import('./pages/Admin/Settings'));
const CarePlanManagement = lazy(() => import('./pages/Admin/CarePlanManagement'));
const Statistics = lazy(() => import('./pages/staff/statistics'));
const CallDashboard = lazy(() => import('./pages/staff/callcenter/callDashboard'));
const MarketingDashboard = lazy(() => import('./pages/staff/marketing/MarketingDashboard'));
const SubmittedLeads = lazy(() => import('./pages/staff/marketing/SubmittedLeads'));
const FilledLeads = lazy(() => import('./pages/staff/FilledLeads'));
const LeadFormPage = lazy(() => import('./pages/LeadFormpage/leadformpage'));
const DocumentViewer = lazy(() => import('./pages/DocumentViewer'));
const PatientList = lazy(() => import('./pages/Admin/PatientList'));
const PatientProfile = lazy(() => import('./pages/Admin/PatientProfile'));
const AgreementManagement = lazy(() => import('./components/agreements/AgreementManagement'));
const AgreementAccess = lazy(() => import('./pages/AgreementAccess'));
const NurseDashboard = lazy(() => import('./pages/Nurse/Dashboard.js'));
const InvoiceList = lazy(() => import('./pages/Invoice/InvoiceList'));
const InvoiceGenerator = lazy(() => import('./pages/Invoice/InvoiceGenerator'));

const AdminLayoutWrapper = () => (
  <AdminLayout>
    <Outlet />
  </AdminLayout>
);

function App() {

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 8,
        },
      }}
    >
      <UserProvider>
        <StaffProvider>
          <Router>
            <Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading..." />
              </div>
            }>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/agreement-access" element={<AgreementAccess />} />
                {/* Route for creating a new lead */}
                <Route path="/leadform/new" element={
                  <ProtectedRoute>
                    <LeadFormPage />
                  </ProtectedRoute>
                } />

                {/* Route for editing an existing lead */}
                <Route
                  path="/leadform/:leadId"
                  element={
                    // 2. Wrap the page element in <ProtectedRoute>
                    <ProtectedRoute>
                      <LeadFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calldashboard"
                  element={
                    <ProtectedRoute>
                      <CallDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/filled-leads" element={
                  <ProtectedRoute>
                    <FilledLeads />
                  </ProtectedRoute>
                } />
                {/* <Route path="/manager/dashboard" element={<StaffDashboard />} /> */}
                {/* <Route path="/leads" element={<LeadForm  />} /> */}
                <Route path="/marketing-dashboard" element={
                  <ProtectedRoute>
                    <MarketingDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/marketing-dashboard/submitted-leads" element={
                  <ProtectedRoute>
                    <SubmittedLeads />
                  </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/platformadmin/login" element={<AdminLogin />} />
                <Route path="/platformadmin/forgot-password" element={<ForgotPassword />} />
                <Route path="/platformadmin/reset-password/:token" element={<ResetPassword />} />
                <Route path="/platformadmin" element={
                  <ProtectedRoute redirectPath='/platformadmin/login'>
                    <AdminLayoutWrapper />
                  </ProtectedRoute>
                }>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="leads" element={<LeadsManagement />} />
                  <Route path="patients" element={<PatientList />} />
                  <Route path="patients/:patientId" element={<PatientProfile />} />
                  <Route path="staff" element={<StaffManagement />} />
                  <Route path="reports" element={<ReportsAndAnalytics />} />
                  <Route path="events" element={<EventManagement />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="care-plans" element={<CarePlanManagement />} />
                  <Route path="invoices" element={<InvoiceList />} />
                  <Route path="invoices/new" element={<InvoiceGenerator />} />
                  <Route path="invoices/edit/:id" element={<InvoiceGenerator />} />
                  <Route path="agreements" element={<AgreementManagement />} />
                  <Route index element={<Navigate to="dashboard" />} />
                </Route>



                <Route element={
                  <ProtectedRoute>
                    <StaffDashboard />
                  </ProtectedRoute>
                }>
                  <Route path="/manager/dashboard" element={<Statistics />} />
                  <Route path="/manager/leads" element={<LeadsManagement />} />
                  <Route path="/manager/patients" element={<PatientList />} />
                  <Route path="/manager/patients/:patientId" element={<PatientProfile />} />
                  <Route path="/manager/care-plans" element={<CarePlanManagement />} />
                  <Route path="/manager/invoices" element={<InvoiceList />} />
                  <Route path="/manager/invoices/new" element={<InvoiceGenerator />} />
                  <Route path="/manager/invoices/edit/:id" element={<InvoiceGenerator />} />
                  <Route path="/manager/settings" element={<Settings />} />
                  <Route path="/reports" element={<ReportsAndAnalytics />} />

                  {/* Optional: Redirect from base /manager to dashboard */}
                  <Route path="/manager" element={<Navigate to="/manager/dashboard" replace />} />
                </Route>



                <Route element={
                  <ProtectedRoute>
                    <NurseDashboard />
                  </ProtectedRoute>
                }>
                  <Route path="/nurse/patients" element={<PatientList />} />
                  <Route path="/nurse/patients/:patientId" element={<PatientProfile />} />
                  <Route path="/nurse/attendance" element={<Attendance />} />
                  {/* Redirects */}
                  <Route path="/nurse/dashboard" element={<Navigate to="/nurse/patients" replace />} />
                  <Route path="/nurse" element={<Navigate to="/nurse/patients" replace />} />
                </Route>


                {/* <Route path='/manager/leads' element={<LeadsManagement/>}/> */}
                {/* Default redirect */}
                <Route path="/view-document/:filePath" element={
                  <ProtectedRoute>
                    <DocumentViewer />
                  </ProtectedRoute>
                } />
                <Route path="/" element={<Navigate to="/login" />} />
              </Routes>
            </Suspense>
          </Router>
        </StaffProvider>
      </UserProvider>
    </ConfigProvider>
  );
}

export default App;